// @ts-strict-ignore
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Inject,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { LMModel, LMModelField } from '@form/model';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMLayoutType } from '@field/services';
import { LMReportFieldPickerService } from '@app/components/report';
import { Observable } from 'rxjs';

@Component({
  selector:    'lm-field',
  templateUrl: './lm-field-layout.component.html',
  styles:      [
    `
    /*Override SOHO field margin bottom */
    /*Margin bottom is on the nested component (lm-text, ect..) for example */
    :host {margin-bottom: 0 !important;}
  `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LMFieldLayoutComponent implements OnInit {

  @Input() layoutObject: LMFormLayoutField;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;
  @Input() placeHolder: any;
  @Input() layoutInfo: LMFormLayoutInfo;
  @Input() lmColumnLayoutInfo: LMColumnLayoutInfo;
  @Input() lmParagraphLayoutInfo: LMParagraphLayoutInfo;
  @Input() parentCompoundFieldDisplayOnly: boolean;
  @Input() parentIsFormLayout: boolean;

  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();
  @Output() errorOnMemberField: EventEmitter<string> = new EventEmitter<string>();

  @HostBinding('class.dropdown-member-field') get parentIsCompoundField() {
    if (this.lmParagraphLayoutInfo && this.lmParagraphLayoutInfo.isCompoundField) {
      return this.layoutType === LMLayoutType.DROPDOWN;
    }
  }

  @HostBinding('style') get style() {

    const style: { [key: string]: unknown } = {};

    if (this.lmReportFieldPickerService?.isCreateReportMode) {
      style.display = 'flex'; 

      if (this.lmParagraphLayoutInfo) {
        style.display = 'inline-flex';
      }
    }

    return style;
  }

  /**
   * Set vertical alignment in a paragraph layout
   * This is needed to properly align certain fields such as checkbox and progress bar
   * see FormLayoutParagraphExamples in UITEST for examples
   */
  @HostBinding('style.vertical-align') get verticalAlign() {
    let align = null;
    const label = this.field.label as LMLabelLayout;
    const isNoLabel = label ? label.nolabel : false;

    if ((this.lmParagraphLayoutInfo && this.lmParagraphLayoutInfo.parentIsParagraphLayout && !this.lmParagraphLayoutInfo.isCompoundField)
      && ((this.layoutType === LMLayoutType.CHECKBOX && isNoLabel)
        || this.layoutType === LMLayoutType.PROGRESS)
    ) {
      align = 'bottom';
    }

    if (this.lmFieldServiceAdapter.lmGlobalPersonalizationService.getGlobalPersonalization().useCompactFormLayout) {
      // Was already set by previous check
      if (align !== null) {
        return align;
      }

      align = this.layoutType === LMLayoutType.RADIO || this.field.tagStyle ? 'top' : null;
      // align = this.layoutType === LMLayoutType.DROPDOWN || this.layoutType === LMLayoutType.MULTI_SELECT ? 'top' : null;
    }

    return align;
  }

  @HostBinding('class.lm-compound-field-wrapper') get fieldIsCompactFormatWrapperClass() {
    return this.specField ? (this.specField && this.specField.compactFormat) : false;
  }

  @HostBinding('class.lm-compact-format-by-form-wrapper') get compactFormatByFormWrapperClass() {
    return (this.field && this.field.type) ? this.field.type === 'compactFormatByForm' : false;
  }

  @HostBinding('class.lm-checkbox-field') get checkboxFieldClass() {
    return (this.layoutType && this.layoutType === LMLayoutType.CHECKBOX);
  }

  @HostBinding('class.lm-number-label-field-container') numberLabelFieldContainerClass = false;

  public field: LMFormLayoutField;
  public specField: LMSpecField;
  public hasFieldLayout = false;
  public fieldLayout: LMFormLayout;
  public layoutEnum;
  public layoutType: LMLayoutType;
  public mutexSpecField = null;

  isCreateReportMode$: Observable<boolean>;

  constructor(
    public elementRef: ElementRef,
    private lmLoggerService: LMLoggerService,
    @Inject(forwardRef(() => LMFieldServiceAdapter)) private lmFieldServiceAdapter, // LMFieldServiceAdapter,
    private lmReportFieldPickerService: LMReportFieldPickerService
  ) {
    this.layoutEnum = LMLayoutType;
  }

  private setMutexFieldLayout(fieldId: string) {
    const mutexField: LMModelField = this.model.getField(fieldId);
    if (mutexField == null) {
      this.lmLoggerService.log('Unable to obtain mutexFieldId: bindId = ' + fieldId);
      return null;
    }
    const mutexFieldId = mutexField.mutexFieldId;
    const mutexSpecField = this.specFields[ mutexFieldId ];
    if (mutexSpecField == null) {
      this.lmLoggerService.log('Unable to obtain mutexSpecField: mutexFieldId = ' + mutexFieldId);
      return null;
    }

    const innerMutexField = this.getMutexFieldForLayout(mutexFieldId, mutexSpecField.layout);
    // LMCLIENT-29463 - patterned to 3.5 landmarkForm.buildField()
    // overwrite the specfield's label with the mutexField label.
    if (innerMutexField) {
      if (this.field.label) {
        innerMutexField.label = this.field.label;
      }

      if (this.field.visibility) {
        innerMutexField.visibility = this.field.visibility;
      }
    }
    if (mutexSpecField.layout) {
      this.hasFieldLayout = true;
      this.fieldLayout = mutexSpecField.layout;
    } else {
      this.layoutType = this.lmFieldServiceAdapter.lmFieldLayoutService.specFieldToLayoutType(this.field, mutexSpecField);
    }

    this.mutexSpecField = mutexSpecField;
  }

  private getMutexFieldForLayout(bindId: string, layout: LMFormLayout): any {
    if (!layout) {
      return null;
    }
    const sections = layout.sections as any;
    if (!sections) {
      return null;
    }
    for (let i = 0, layoutLen = sections.length; i < layoutLen; i++) {
      if (sections[i].columns) {
        const column = sections[i].columns;

        if (Array.isArray(column)) {
          for (let j = 0, columnLen = column.length; j < columnLen; j++) {
            if (column[j].elements) {
              const element = column[j].elements;

              for (let k = 0, elementLen = column.length; k < elementLen; k++) {
                if (element[k].field) {
                  const field = element[k].field;
                  if (bindId === field.bindId) {
                    return field;
                  }
                }
              }
            }
          }
        } else {
          if (column.elements) {
            const element = column.elements;
            for (let l = 0, elementLen = element.length; l < elementLen; l++) {
              if (element[l].field) {
                const field = element[l].field;
                if (bindId === field.bindId) {
                  return field;
                }
              }
            }
          }
        }
      }
    }
    return null;
  }

  ngOnInit() {
    this.field = this.layoutObject;

    if (this.field.implementsMutuallyExclusive) {
      this.setMutexFieldLayout(this.field.bindId);
      // no need to continue, the template
      // will process mutex field layout which will add the
      // mutex field(s) to the form.
      return;
    }

    this.specField = (this.field.bindId !== undefined)
      ? this.specFields[ this.field.bindId ]
      : null;

    this.layoutType = this.lmFieldServiceAdapter.lmFieldLayoutService.getLayoutType(this.field, this.specField, this.model, this.lmParagraphLayoutInfo);

    if (this.field.numberLabel) {
      this.numberLabelFieldContainerClass = this.field.numberLabel;
    }

    this.isCreateReportMode$ = this.lmReportFieldPickerService.isCreateReportMode$;
  }

  onFieldEvent(event) {
    this.fieldEvent.emit(event);
  }

  onMemberFieldError(event) {
    this.errorOnMemberField.emit(event);
  }
}
